import { getCookieDomain, isAppHost } from "@/configs";

const AUTH_COOKIE_NAME = "_book_t";
const AUTH_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 365;

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const parts = document.cookie.split("; ");
  let value: string | null = null;
  for (const part of parts) {
    const index = part.indexOf("=");
    if (index < 0) continue;
    const key = part.slice(0, index);
    if (key !== name) continue;
    const raw = part.slice(index + 1);
    try {
      value = decodeURIComponent(raw);
    } catch {
      value = raw;
    }
  }
  return value;
}

export const AUTH_CLEARED_EVENT = "auth-cleared";

export function normalizeSessionToken(raw: string): string {
  let token = raw.trim().replace(/^Bearer\s+/i, "");
  token = token.replace(/[\r\n]/g, "");
  if (token.includes(" ")) {
    token = token.replace(/ /g, "+");
  }
  return token.trim();
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const jsonPayload = decodeURIComponent(
      atob(padded)
        .split("")
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join(""),
    );
    return JSON.parse(jsonPayload);
  } catch {
    return null;
  }
}

export function isUsableSessionToken(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload) return false;
  return typeof payload.email === "string" && payload.email.length > 0 && typeof payload.nonce === "string" && payload.nonce.length > 0;
}

export function clearSession(): void {
  removeAuthToken();
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(AUTH_CLEARED_EVENT));
  }
}

export function isInvalidSessionError(err: any): boolean {
  const status = err?.response?.status;
  const message = String(err?.response?.data?.error || err?.response?.data?.message || err?.message || "");
  if (status === 401) return true;
  return /invalid session token/i.test(message);
}

export function handleAuthError(err: any): boolean {
  if (!isInvalidSessionError(err)) return false;
  clearSession();
  return true;
}

export function getAuthToken(): string | null {
  const token = readCookie(AUTH_COOKIE_NAME);
  if (!token) return null;
  const normalized = normalizeSessionToken(token);
  if (!normalized || !isUsableSessionToken(normalized)) {
    removeAuthToken();
    return null;
  }
  return normalized;
}

export function consumeAuthTokenFromUrl(): boolean {
  if (typeof window === "undefined") return false;

  const url = new URL(window.location.href);
  const raw = url.searchParams.get(AUTH_COOKIE_NAME);
  if (!raw) return false;

  const token = normalizeSessionToken(raw);
  url.searchParams.delete(AUTH_COOKIE_NAME);
  const search = url.searchParams.toString();
  const next = `${url.pathname}${search ? `?${search}` : ""}${url.hash}`;
  window.history.replaceState(window.history.state, "", next);

  if (!token || !isUsableSessionToken(token)) {
    removeAuthToken();
    return false;
  }

  setAuthToken(token);
  return true;
}

function cookieBase(value: string, maxAgeSeconds: number): string[] {
  const parts = [`${AUTH_COOKIE_NAME}=${value}`, "path=/", `max-age=${maxAgeSeconds}`, "samesite=lax"];
  if (typeof window !== "undefined" && window.location.protocol === "https:") {
    parts.push("secure");
  }
  return parts;
}

export function setAuthToken(token: string): void {
  if (typeof document === "undefined") return;

  const value = normalizeSessionToken(token);
  if (!value || !isUsableSessionToken(value)) return;

  const hostname = window.location.hostname;
  const encoded = encodeURIComponent(value);

  // Clear host-only and domain copies so a stale cookie is not read first.
  document.cookie = cookieBase("", 0).join("; ");
  const cookieDomain = getCookieDomain();
  if (cookieDomain && isAppHost(hostname)) {
    document.cookie = [...cookieBase("", 0), `domain=${cookieDomain}`].join("; ");
    document.cookie = [...cookieBase(encoded, AUTH_COOKIE_MAX_AGE_SECONDS), `domain=${cookieDomain}`].join("; ");
  } else {
    document.cookie = cookieBase(encoded, AUTH_COOKIE_MAX_AGE_SECONDS).join("; ");
  }
}

export function removeAuthToken(): void {
  if (typeof document === "undefined") return;

  const hostname = window.location.hostname;
  const parts = [`${AUTH_COOKIE_NAME}=`, "path=/", "max-age=0", "samesite=lax"];

  const cookieDomain = getCookieDomain();
  if (cookieDomain && isAppHost(hostname)) {
    parts.push(`domain=${cookieDomain}`);
  }

  document.cookie = parts.join("; ");
  // Also clear host-only cookie if one was set during local/dev use.
  document.cookie = `${AUTH_COOKIE_NAME}=; path=/; max-age=0; samesite=lax`;
}

export function hasAuthToken(): boolean {
  return !!getAuthToken();
}

// Read email from the signed-in JWT payload (client-side convenience only).
export function getAuthEmail(): string | null {
  const token = getAuthToken();
  if (!token) return null;
  const payload = decodeJwtPayload(token);
  if (!payload || typeof payload.email !== "string" || !payload.email) return null;
  return payload.email;
}
